import {
  HttpErrorResponse,
  HttpEvent,
  HttpInterceptorFn,
  HttpStatusCode,
} from '@angular/common/http';
import { inject, NgZone } from '@angular/core';
import { from, switchMap, mergeMap, Observable, tap } from 'rxjs';
import { AuthService } from '../services/auth-service';
import { environment } from '../../../environments/environment';

/**
 * HTTP interceptor that attaches Firebase Auth token to all API requests.
 * Uses functional interceptor pattern (Angular 20+).
 *
 * IMPORTANT: Waits for Firebase auth state to be restored from persistence
 * before attempting to get the token. This prevents missing Authorization
 * headers on the first requests after page load.
 *
 * A 401 on a request that carried a token means the session has expired, so the
 * user is signed out and sent to sign-in instead of polling with a dead token.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const ngZone = inject(NgZone, { optional: true });
  const apiBaseUrl = (environment.apiUrl || '').replace(/\/+$/, '');

  if (!shouldAttachAuthHeader(req.url, apiBaseUrl)) {
    return next(req);
  }

  return from(authService.waitForAuthState()).pipe(
    mergeMap(() => from(authService.getIdToken())),
    switchMap((token) => {
      const headers: Record<string, string> = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
        headers['X-Firebase-Authorization'] = `Bearer ${token}`;
      }
      const authenticatedRequest = token ? req.clone({ setHeaders: headers }) : req;
      return new Observable<HttpEvent<unknown>>((observer) => {
        const sub = next(authenticatedRequest).subscribe({
          next: (val) => {
            if (ngZone) {
              ngZone.run(() => observer.next(val));
            } else {
              observer.next(val);
            }
          },
          error: (err) => {
            if (ngZone) {
              ngZone.run(() => observer.error(err));
            } else {
              observer.error(err);
            }
          },
          complete: () => {
            if (ngZone) {
              ngZone.run(() => observer.complete());
            } else {
              observer.complete();
            }
          },
        });
        return () => sub.unsubscribe();
      }).pipe(
        tap({
          error: (error: unknown) => {
            if (token && isUnauthorized(error)) {
              void authService.expireSession();
            }
          },
        }),
      );
    }),
  );
};

function isUnauthorized(error: unknown): boolean {
  return error instanceof HttpErrorResponse && error.status === HttpStatusCode.Unauthorized;
}

function shouldAttachAuthHeader(requestUrl: string, apiBaseUrl: string): boolean {
  try {
    const targetUrl = new URL(requestUrl, window.location.origin);
    const expectedOrigin = apiBaseUrl
      ? new URL(apiBaseUrl, window.location.origin).origin
      : window.location.origin;

    const isTargetingExpectedOrigin = targetUrl.origin === expectedOrigin;
    const isApiPath = targetUrl.pathname.startsWith('/api/');

    return isTargetingExpectedOrigin && isApiPath;
  } catch {
    return false;
  }
}
